/**
 * Options Panel
 * BTC/ETH only: max pain, put/call ratio, options OI
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LoadingSkeleton } from './loading-skeleton';
import { useCoinglassOptions } from '@/hooks/use-coinglass';
import { formatCompact, formatPercent } from '@/lib/utils';
import { useAlertStore, getFlatPrice } from '@/stores/alert-store';
import type { Asset } from '@/stores/alert-store';

interface OptionsPanelProps {
  asset: Asset;
}

export function OptionsPanel({ asset }: OptionsPanelProps) {
  const { data, isLoading } = useCoinglassOptions(asset);
  const prices = useAlertStore((s) => s.prices[asset]);
  const currentPrice = getFlatPrice(prices);

  if (asset !== 'BTC' && asset !== 'ETH') return null;
  if (isLoading) return <LoadingSkeleton className="h-48" lines={4} />;

  const opts = data?.optionsData;
  if (!opts) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Options ({asset})</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No options data</p>
        </CardContent>
      </Card>
    );
  }

  const maxPainDiff = currentPrice > 0 && opts.maxPain ? ((currentPrice - opts.maxPain) / opts.maxPain) * 100 : 0;
  const maxPainDirection = maxPainDiff > 0 ? 'above' : 'below';

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Options ({asset})</CardTitle>
          <Badge variant={(opts.putCallRatio ?? 0) > 1 ? 'bearish' : 'bullish'}>
            P/C: {(opts.putCallRatio ?? 0).toFixed(2)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Max Pain */}
        <div>
          <p className="text-xs text-muted-foreground">Max Pain</p>
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold">${formatCompact(opts.maxPain)}</span>
            {currentPrice > 0 && (
              <span className={`text-xs ${maxPainDiff > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {formatPercent(Math.abs(maxPainDiff))} {maxPainDirection}
              </span>
            )}
          </div>
        </div>

        {/* OI Breakdown */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <span className="text-muted-foreground">Call OI: </span>
            <span className="text-green-500">${formatCompact(opts.callOI)}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Put OI: </span>
            <span className="text-red-500">${formatCompact(opts.putOI)}</span>
          </div>
        </div>

        {/* Total OI + Change */}
        <div className="flex justify-between text-xs">
          <span>
            Total OI: <span className="font-medium">${formatCompact(opts.totalOI)}</span>
          </span>
          <span className={opts.oiChange24h > 0 ? 'text-green-500' : 'text-red-500'}>
            {formatPercent(opts.oiChange24h)}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
